import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../api/client";
import { University, Result } from "../types";

function Spinner() {
  return (
    <div className="flex justify-center py-12">
      <div className="w-10 h-10 border-[3px] border-indigo-100 border-t-indigo-600 rounded-full animate-spin" />
    </div>
  );
}

function ScoreBadge({ score }: { score: number }) {
  const cls =
    score > 140
      ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20"
      : score >= 80
      ? "bg-amber-50 text-amber-700 ring-1 ring-amber-600/20"
      : "bg-rose-50 text-rose-700 ring-1 ring-rose-600/20";
  return (
    <span className={`inline-block rounded-full px-3 py-1 text-sm font-semibold ${cls}`}>
      {score}
    </span>
  );
}

const YEARS = [2025, 2024, 2023, 2022, 2021];
const ETYPES = [
  { value: "", label: "Barchasi" },
  { value: "Kunduzgi", label: "Kunduzgi" },
  { value: "Sirtqi", label: "Sirtqi" },
  { value: "Kechki", label: "Kechki" },
  { value: "Masofaviy", label: "Masofaviy" },
];

const socialIcons: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  telegram: {
    label: "Telegram",
    color: "bg-sky-50 text-sky-600 hover:bg-sky-100",
    icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>,
  },
  instagram: {
    label: "Instagram",
    color: "bg-pink-50 text-pink-600 hover:bg-pink-100",
    icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>,
  },
  facebook: {
    label: "Facebook",
    color: "bg-blue-50 text-blue-600 hover:bg-blue-100",
    icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
  },
  youtube: {
    label: "YouTube",
    color: "bg-red-50 text-red-600 hover:bg-red-100",
    icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>,
  },
};

export default function UniversityDetailPage() {
  const { eduId } = useParams<{ eduId: string }>();
  const [uni, setUni] = useState<University | null>(null);
  const [directions, setDirections] = useState<Result[]>([]);
  const [year, setYear] = useState(2025);
  const [etype, setEtype] = useState("");
  const [language, setLanguage] = useState("");
  const [languages, setLanguages] = useState<string[]>([]);
  const [loadingUni, setLoadingUni] = useState(true);
  const [loadingDir, setLoadingDir] = useState(true);

  useEffect(() => {
    if (!eduId) return;
    setLoadingUni(true);
    api
      .get(`/universities/${eduId}`)
      .then((r) => setUni(r.data))
      .catch(() => setUni(null))
      .finally(() => setLoadingUni(false));

    api.get("/filters/languages").then((r) => setLanguages(r.data || [])).catch(() => {});
  }, [eduId]);

  useEffect(() => {
    if (!eduId) return;
    setLoadingDir(true);
    const params: Record<string, string | number> = { year };
    if (etype) params.etype = etype;
    if (language) params.language = language;

    api
      .get(`/universities/${eduId}/directions`, { params })
      .then((r) => setDirections(r.data?.items || r.data || []))
      .catch(() => setDirections([]))
      .finally(() => setLoadingDir(false));
  }, [eduId, year, etype, language]);

  if (loadingUni) return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10"><Spinner /></div>;

  if (!uni) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-slate-700 text-lg font-semibold mb-1">Universitet topilmadi</p>
        <Link to="/universities" className="inline-flex items-center gap-1 mt-4 text-indigo-600 hover:text-indigo-700 font-medium text-sm">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          Universitetlar ro'yxatiga qaytish
        </Link>
      </div>
    );
  }

  const activeSocials = (["telegram", "instagram", "facebook", "youtube"] as const).filter(
    (key) => uni[key]
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-slate-500 mb-6">
        <Link to="/universities" className="hover:text-indigo-600 transition-colors font-medium">Universitetlar</Link>
        <svg className="w-4 h-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        <span className="text-slate-800 font-medium truncate max-w-xs">{uni.name}</span>
      </nav>

      {/* University Hero Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden mb-8">
        {/* Gradient header strip */}
        <div className="h-2 bg-gradient-to-r from-indigo-600 to-indigo-800" />

        <div className="p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row gap-6">
            {/* Logo */}
            {uni.logo_url ? (
              <img
                src={uni.logo_url}
                alt={uni.name}
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-contain bg-slate-50 border border-slate-100 flex-shrink-0"
              />
            ) : (
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-gradient-to-br from-indigo-50 to-indigo-100 flex items-center justify-center flex-shrink-0 border border-indigo-100/50">
                <span className="text-indigo-600 font-bold text-3xl">
                  {uni.name?.charAt(0) || "U"}
                </span>
              </div>
            )}

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 leading-tight">{uni.name}</h1>

              <div className="flex flex-wrap gap-2 mb-4">
                {uni.region_name && (
                  <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 rounded-lg px-3 py-1.5 text-xs font-medium">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {uni.region_name}
                  </span>
                )}
                {uni.uni_type && (
                  <span className={`inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-medium ${
                    uni.uni_type === "Davlat" ? "bg-indigo-50 text-indigo-700" : "bg-purple-50 text-purple-700"
                  }`}>
                    {uni.uni_type}
                  </span>
                )}
              </div>

              {/* Contact cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {uni.phone && (
                  <a href={`tel:${uni.phone}`} className="flex items-center gap-3 bg-slate-50 rounded-xl p-3 hover:bg-slate-100 transition-colors group">
                    <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shadow-sm">
                      <svg className="w-4.5 h-4.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Telefon</p>
                      <p className="text-sm text-slate-700 group-hover:text-indigo-600 transition-colors">{uni.phone}</p>
                    </div>
                  </a>
                )}
                {uni.email && (
                  <a href={`mailto:${uni.email}`} className="flex items-center gap-3 bg-slate-50 rounded-xl p-3 hover:bg-slate-100 transition-colors group">
                    <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shadow-sm">
                      <svg className="w-4.5 h-4.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Email</p>
                      <p className="text-sm text-slate-700 group-hover:text-indigo-600 transition-colors truncate">{uni.email}</p>
                    </div>
                  </a>
                )}
                {uni.website && (
                  <a href={uni.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-slate-50 rounded-xl p-3 hover:bg-slate-100 transition-colors group">
                    <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shadow-sm">
                      <svg className="w-4.5 h-4.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Veb-sayt</p>
                      <p className="text-sm text-slate-700 group-hover:text-indigo-600 transition-colors truncate">{uni.website}</p>
                    </div>
                  </a>
                )}
                {uni.address && (
                  <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-3">
                    <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shadow-sm flex-shrink-0">
                      <svg className="w-4.5 h-4.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Manzil</p>
                      <p className="text-sm text-slate-700">{uni.address}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Social media */}
              {activeSocials.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {activeSocials.map((key) => {
                    const social = socialIcons[key];
                    return (
                      <a
                        key={key}
                        href={uni[key] || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${social.color}`}
                      >
                        {social.icon}
                        {social.label}
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Directions section */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="p-5 sm:p-6 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Yo'nalishlar</h2>

          {/* Filters row */}
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Year tabs */}
            <div className="flex bg-slate-100 rounded-xl p-1 gap-0.5">
              {YEARS.map((y) => (
                <button
                  key={y}
                  onClick={() => setYear(y)}
                  className={`px-4 py-2 text-xs font-semibold rounded-lg transition-all ${
                    year === y
                      ? "bg-white text-indigo-700 shadow-sm"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {y}
                </button>
              ))}
            </div>
            <select
              value={etype}
              onChange={(e) => setEtype(e.target.value)}
              className="border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              {ETYPES.map((et) => (
                <option key={et.value} value={et.value}>{et.label}</option>
              ))}
            </select>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="">Barcha tillar</option>
              {languages.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="p-5 sm:p-6">
          {loadingDir ? (
            <Spinner />
          ) : directions.length > 0 ? (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left">
                      <th className="pb-3 pr-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Yo'nalish</th>
                      <th className="pb-3 pr-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Shakl</th>
                      <th className="pb-3 pr-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Til</th>
                      <th className="pb-3 pr-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">Grand ball</th>
                      <th className="pb-3 pr-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">Grand kvota</th>
                      <th className="pb-3 pr-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">Kontrakt ball</th>
                      <th className="pb-3 text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">Kontrakt kvota</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {directions.map((d, i) => (
                      <tr
                        key={`${d.id}-${d.direction_id}-${i}`}
                        className="hover:bg-slate-50/50 transition-colors"
                      >
                        <td className="py-3.5 pr-4 font-medium text-slate-900 max-w-[280px]">{d.direction_name}</td>
                        <td className="py-3.5 pr-4">
                          <span className="bg-indigo-50 text-indigo-700 rounded-lg px-2 py-0.5 text-xs font-medium">{d.etype}</span>
                        </td>
                        <td className="py-3.5 pr-4 text-slate-600">{d.language}</td>
                        <td className="py-3.5 pr-4 text-center"><ScoreBadge score={d.grand_ball} /></td>
                        <td className="py-3.5 pr-4 text-center text-slate-600">{d.grand_kvota}</td>
                        <td className="py-3.5 pr-4 text-center"><ScoreBadge score={d.kontrakt_ball} /></td>
                        <td className="py-3.5 text-center text-slate-600">{d.kontrakt_kvota}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3">
                {directions.map((d, i) => (
                  <div
                    key={`${d.id}-${d.direction_id}-${i}`}
                    className="border border-slate-200 rounded-xl p-4"
                  >
                    <h3 className="font-semibold text-slate-900 text-sm mb-2 leading-snug">{d.direction_name}</h3>
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-lg text-xs font-medium">{d.etype}</span>
                      <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-lg text-xs font-medium">{d.language}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Grant</p>
                        <ScoreBadge score={d.grand_ball} />
                        <p className="text-[10px] text-slate-400 mt-1">kvota: {d.grand_kvota}</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Kontrakt</p>
                        <ScoreBadge score={d.kontrakt_ball} />
                        <p className="text-[10px] text-slate-400 mt-1">kvota: {d.kontrakt_kvota}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className="text-slate-700 font-semibold mb-0.5">Ma'lumot topilmadi</p>
              <p className="text-slate-400 text-sm">Bu yil uchun yo'nalishlar mavjud emas</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
